import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from '../admin.service';
import notify from 'devextreme/ui/notify';

@Component({
  selector: 'app-user-edit',
  standalone: false,
  templateUrl: './user-edit.component.html',
  styleUrls: ['./user-edit.component.css']
})
export class UserEditComponent implements OnInit {
  isNew = false;
  userId: number | null = null;
  formData: any = { name: '', email: '', password: '', theme: 0 };
  grants: any[] = [];                 // current user_location_role grants (from the user-list payload)
  roles: any[] = [];
  locations: any[] = [];
  newGrant: any = { location_id: null, role_id: null };

  constructor(
    private admin: AdminService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.admin.getRoles().subscribe(r => this.roles = this.unwrap(r));
    this.admin.getLocations().subscribe(l => this.locations = this.unwrap(l));

    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.userId = +idParam;
      this.loadUser(this.userId);
    } else {
      this.isNew = true;
    }
  }

  /** API resources may return a bare array or { success, data }. */
  private unwrap(res: any): any[] {
    if (Array.isArray(res)) { return res; }
    return res?.data ?? [];
  }

  private loadUser(id: number): void {
    this.admin.getUsers().subscribe(users => {
      const user = (users || []).find(u => u.id === id);
      if (!user) {
        notify('User not found', 'error', 2500);
        this.router.navigate(['/admin']);
        return;
      }
      this.formData = { name: user.name, email: user.email, password: '', theme: user.theme ?? 0 };
      this.grants = [];
      (user.locations || []).forEach((loc: any) =>
        (loc.roles || []).forEach((r: any) => {
          this.grants.push({
            location_id: loc.location_id,
            location_name: loc.name,
            location_label: loc.name || loc.location_id,
            role_id: r.id,
            role_code: r.code,
            role_name: r.name,
            role_label: r.name || r.code
          });
        }));
    });
  }

  save(): void {
    if (this.isNew) {
      this.admin.createUser({
        name: this.formData.name,
        email: this.formData.email,
        password: this.formData.password
      }).subscribe(() => {
        notify('User created', 'success', 2000);
        this.router.navigate(['/admin']);
      }, err => notify(err?.error?.message || 'Unable to create user', 'error', 2500));
    } else {
      const payload: any = { email: this.formData.email, username: this.formData.name, theme: this.formData.theme };
      if (this.formData.password) { payload.password = this.formData.password; }
      this.admin.updateUser(payload).subscribe(() => {
        notify('User updated', 'success', 2000);
        this.router.navigate(['/admin']);
      }, err => notify(err?.error?.message || 'Unable to update user', 'error', 2500));
    }
  }

  cancel(): void {
    this.router.navigate(['/admin']);
  }

  addGrant(): void {
    if (!this.userId || !this.newGrant.location_id || !this.newGrant.role_id) {
      notify('Pick a location and a role first', 'warning', 2000);
      return;
    }
    this.admin.assignRole({
      user_id: this.userId,
      location_id: this.newGrant.location_id,
      role_id: this.newGrant.role_id
    }).subscribe(() => {
      notify('Role assigned', 'success', 2000);
      this.loadUser(this.userId!);
      this.newGrant = { location_id: null, role_id: null };
    }, err => notify(err?.error?.message || 'Unable to assign role', 'error', 2500));
  }

  removeGrant(grant: any): void {
    if (!this.userId) { return; }
    this.admin.removeRole({
      user_id: this.userId,
      location_id: grant.location_id,
      role_id: grant.role_id
    }).subscribe(() => {
      notify('Role removed', 'success', 2000);
      this.loadUser(this.userId!);
    }, err => notify(err?.error?.message || 'Unable to remove role', 'error', 2500));
  }

  // Bound as an arrow property so `this` resolves inside the dx grid button callback.
  onRemoveClick = (e: any): void => { this.removeGrant(e.row.data); };
}
